import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, Send, Sparkles, X, ChevronRight } from "lucide-react";

const STEPS = [
  {
    icon: UploadCloud,
    title: "Upload your credit report",
    body: "Drop in a PDF from Credit Karma, myFICO, IdentityIQ, or your bureau. Nothing leaves your account.",
    cta: "Take me to Upload",
    path: "/app/upload",
    highlight: "step 1 of 3",
  },
  {
    icon: FileText,
    title: "Review the negative accounts we found",
    body: "We pull out every collection, charge-off, late payment, and inquiry — then let you tag the reason each one is inaccurate.",
    cta: "See what a review looks like",
    path: "/app/upload",
    highlight: "step 2 of 3",
  },
  {
    icon: Send,
    title: "Generate & mail your dispute letters",
    body: "One click produces bureau letters, furnisher letters, and CFPB complaints — with your name and address auto-filled. Mail with Lob or download PDFs.",
    cta: "Finish tour",
    path: "/app",
    highlight: "step 3 of 3",
  },
];

export default function OnboardingTour({ onDismiss }) {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [dismissing, setDismissing] = useState(false);

  const finish = async (destPath) => {
    setDismissing(true);
    try {
      await api.put("/me/onboarding", { completed: true });
    } catch { /* non-fatal */ }
    if (onDismiss) onDismiss();
    if (destPath) nav(destPath);
  };

  const step = STEPS[i];
  const Icon = step.icon;
  const isLast = i === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm grid place-items-center px-4" data-testid="onboarding-overlay">
      <div className="w-full max-w-lg rounded-3xl bg-white border border-slate-200 shadow-2xl overflow-hidden">
        <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-purple-600"/>
              <div className="text-[10px] uppercase tracking-widest text-purple-700 font-semibold">Welcome · {step.highlight}</div>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mt-1 leading-tight">{step.title}</h2>
          </div>
          <button
            onClick={() => finish(null)}
            className="text-slate-400 hover:text-slate-700 transition -mr-1 -mt-1 p-1 rounded-lg"
            aria-label="Skip tour"
            data-testid="onboarding-skip"
            disabled={dismissing}
          ><X size={18}/></button>
        </div>

        <div className="px-6 py-6 flex items-start gap-4">
          <div className="shrink-0 h-11 w-11 rounded-2xl grid place-items-center bg-purple-50 text-purple-700 ring-1 ring-purple-100">
            <Icon size={20}/>
          </div>
          <p className="text-slate-700 leading-relaxed text-[15px]">{step.body}</p>
        </div>

        <div className="px-6 pb-5 flex items-center gap-1.5">
          {STEPS.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-purple-600" : idx < i ? "w-4 bg-purple-300" : "w-4 bg-slate-200"}`}
            />
          ))}
        </div>

        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="text-slate-600 hover:text-slate-900"
            onClick={() => finish(null)}
            disabled={dismissing}
            data-testid="onboarding-skip-bottom"
          >Skip tour</Button>
          <div className="flex gap-2">
            {i > 0 && (
              <Button
                variant="outline"
                className="rounded-xl border-slate-200 text-slate-800"
                onClick={() => setI(i - 1)}
                data-testid="onboarding-back"
              >Back</Button>
            )}
            {!isLast ? (
              <Button
                className="btn-primary rounded-xl"
                onClick={() => setI(i + 1)}
                data-testid="onboarding-next"
              >Next<ChevronRight size={14} className="ml-0.5"/></Button>
            ) : (
              <Button
                className="btn-primary rounded-xl"
                onClick={() => finish(step.path)}
                disabled={dismissing}
                data-testid="onboarding-finish"
              >{step.cta}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
